import { FONT_MAP } from '../constants/defaults';

// Linear interpolation
export function lerp(start, end, t) {
  return start + (end - start) * t;
}

// Darken a hex color
export function darkenColor(hex, amount = 0.2) {
  const num = parseInt(hex.slice(1), 16);
  const r = Math.max(0, ((num >> 16) & 0xff) * (1 - amount));
  const g = Math.max(0, ((num >> 8) & 0xff) * (1 - amount));
  const b = Math.max(0, (num & 0xff) * (1 - amount));
  return `#${Math.round(r).toString(16).padStart(2, '0')}${Math.round(g).toString(16).padStart(2, '0')}${Math.round(b).toString(16).padStart(2, '0')}`;
}

// Lighten a hex color
export function lightenColor(hex, amount = 0.2) {
  const num = parseInt(hex.slice(1), 16);
  const r = Math.min(255, ((num >> 16) & 0xff) + (255 - ((num >> 16) & 0xff)) * amount);
  const g = Math.min(255, ((num >> 8) & 0xff) + (255 - ((num >> 8) & 0xff)) * amount);
  const b = Math.min(255, (num & 0xff) + (255 - (num & 0xff)) * amount);
  return `#${Math.round(r).toString(16).padStart(2, '0')}${Math.round(g).toString(16).padStart(2, '0')}${Math.round(b).toString(16).padStart(2, '0')}`;
}

// Get contrasting text color
export function getContrastColor(hex) {
  const num = parseInt(hex.slice(1), 16);
  const r = (num >> 16) & 0xff;
  const g = (num >> 8) & 0xff;
  const b = num & 0xff;
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? '#18181b' : '#fafafa';
}

// Compute preview styles from form data
export function computePreviewStyles(formData) {
  const { visuals, scales } = formData;
  
  const isDark = visuals.colorMode === 'dark';
  const t_dense = scales.minimalistDense / 100;
  const t_serious = scales.playfulSerious / 100;
  const t_experimental = scales.safeExperimental / 100;

  // Hue rotation for playful end
  const hueRotate = lerp(15, 0, t_serious);
  
  // Experimental end effects
  const shadowIntensity = lerp(0, 20, t_experimental);
  const borderWidth = lerp(1, 3, t_experimental);
  const skewAmount = lerp(0, 1.5, t_experimental);

  return {
    '--accent': visuals.colorSeed,
    '--accent-dark': darkenColor(visuals.colorSeed, 0.15),
    '--accent-light': lightenColor(visuals.colorSeed, 0.3),
    '--accent-contrast': getContrastColor(visuals.colorSeed),
    '--bg': isDark ? '#18181b' : '#fafafa',
    '--bg-secondary': isDark ? '#27272a' : '#f4f4f5',
    '--bg-tertiary': isDark ? '#3f3f46' : '#e4e4e7',
    '--text': isDark ? '#fafafa' : '#18181b',
    '--text-secondary': isDark ? '#a1a1aa' : '#71717a',
    '--text-muted': isDark ? '#71717a' : '#a1a1aa',
    '--border': isDark ? '#3f3f46' : '#e4e4e7',
    '--font': FONT_MAP[visuals.fontStyle],
    '--radius': `${lerp(1, 0.25, t_dense)}rem`,
    '--radius-sm': `${lerp(0.5, 0.125, t_dense)}rem`,
    '--padding': `${lerp(2, 0.75, t_dense)}rem`,
    '--padding-sm': `${lerp(1, 0.375, t_dense)}rem`,
    '--gap': `${lerp(1.5, 0.5, t_dense)}rem`,
    '--transition': `${lerp(0.4, 0.15, t_serious)}s`,
    '--hue-rotate': `${hueRotate}deg`,
    '--shadow': `0 ${shadowIntensity}px ${shadowIntensity * 2}px rgba(0,0,0,${isDark ? 0.4 : 0.1})`,
    '--border-width': `${borderWidth}px`,
    '--skew': `${skewAmount}deg`
  };
}

// Convert styles object to CSS string
export function stylesToCSS(styles) {
  return Object.entries(styles)
    .map(([key, value]) => `${key}: ${value}`)
    .join('; ');
}
